/**
 * MaintenanceWidget — kompaktes Dashboard-Widget für die Wartungsplanung
 * (Spec §6), Muster: VisitReminderWidget.tsx.
 *
 * Zeigt zwei Zähler aus `GET /maintenance/summary`:
 *   - Rot    → Wartung abgelaufen oder innerhalb der roten Frist
 *   - Orange → innerhalb der orangenen Vorwarnfrist
 *
 * Gezählt wird, wofür der angemeldete Nutzer ZUSTÄNDIG ist (Sichtbarkeitsregel
 * wie in den übrigen Modulen, `lib/visibility.ts`); Geschäftsleitung und Admin
 * sehen weiterhin alles. Vorher zählte die Kachel global — ein Bereichsleiter
 * sah dort auch Systeme, für die er nichts kann. Ein Segment-Filter bleibt
 * bewusst draußen: Das Widget ist der Einstieg, die Segment-Eingrenzung
 * passiert auf der Seite /wartung selbst.
 *
 * Klick auf einen Zähler navigiert zu /wartung?filter=<color> — die Seite
 * fragt die Liste dann serverseitig auf diese Ampel-Farbe gefiltert ab und
 * zeigt damit genau die Zeilen, die hier gezählt wurden (der Server lässt den
 * Zeitraum in diesem Fall weg, siehe `listExpiring`).
 */
import { Badge, Box, Group, Loader, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMaintenanceSummary } from "../../hooks/useMaintenance.js";

type MaintenanceColor = "red" | "orange";

interface ColorConfig {
  color: string;
  labelKey: string;
  descriptionKey: string;
}

const COLOR_CONFIG: Record<MaintenanceColor, ColorConfig> = {
  red: {
    color: "red",
    labelKey: "pages.maintenanceWidget.red",
    descriptionKey: "pages.maintenanceWidget.redDescription",
  },
  orange: {
    color: "orange",
    labelKey: "pages.maintenanceWidget.orange",
    descriptionKey: "pages.maintenanceWidget.orangeDescription",
  },
};

interface CounterProps {
  color: MaintenanceColor;
  count: number;
  onClick: () => void;
}

function MaintenanceCounter({ color, count, onClick }: CounterProps) {
  const { t } = useTranslation();
  const config = COLOR_CONFIG[color];
  return (
    <Paper p="sm" withBorder style={{ cursor: "pointer", flex: 1 }} onClick={onClick}>
      <Stack gap={4} align="center">
        <ThemeIcon color={config.color} variant="light" size="xl" radius="md">
          <Text fw={700} size="lg" c={config.color}>
            {count}
          </Text>
        </ThemeIcon>
        <Badge color={config.color} variant="light" size="sm">
          {t(config.labelKey)}
        </Badge>
        <Text size="xs" c="dimmed" ta="center">
          {t(config.descriptionKey)}
        </Text>
      </Stack>
    </Paper>
  );
}

export function MaintenanceWidget({ onlyMine }: { onlyMine: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMaintenanceSummary({ onlyMine });

  function handleFilterClick(filter: MaintenanceColor) {
    // Die Sicht reist mit — sonst zählt die Kachel eine andere Menge, als
    // die Wartungsseite danach zeigt.
    void navigate({ to: "/wartung", search: { filter, onlyMine } as never });
  }

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {t("pages.maintenanceWidget.loading")}
          </Text>
        </Stack>
      </Paper>
    );
  }

  if (isError || !data) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          {t("pages.maintenanceWidget.error")}
        </Text>
      </Paper>
    );
  }

  if (data.red === 0 && data.orange === 0) {
    return (
      <Box>
        <Text fw={600} mb="sm">
          {t("pages.maintenanceWidget.title")}
        </Text>
        <Text size="sm" c="dimmed">
          {t("pages.maintenanceWidget.empty")}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fw={600} mb="sm">
        {t("pages.maintenanceWidget.title")}
      </Text>
      <Group grow gap="sm">
        <MaintenanceCounter
          color="red"
          count={data.red}
          onClick={() => handleFilterClick("red")}
        />
        <MaintenanceCounter
          color="orange"
          count={data.orange}
          onClick={() => handleFilterClick("orange")}
        />
      </Group>
    </Box>
  );
}
